import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import { useScripts } from "pages/DevelopWorkspace/contexts/ScriptsContext/ScriptsContext";
import { getDocumentScript } from "pages/DevelopWorkspace/helpers/getDocumentScript";

import { ConfirmationModal } from "components/ConfirmationModal/ConfirmationModal";

interface Modal {
  title: string;
  message: string;
  confirmButtonText: string;
  onConfirm: () => void;
}

const useDocumentsActionConfirmation = () => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);
  const {
    state: { documents },
    actions: { removeDocument, removeAllDocuments },
  } = useDocuments();
  const { state: scriptsState } = useScripts();

  const handleDeleteAllDocuments = (exceptDocumentIds: string[]) => {
    const someDocumentHasContent = documents.some(document => {
      if (exceptDocumentIds.includes(document.id)) {
        return false;
      }

      const script = getDocumentScript(document, scriptsState);

      // We check editorState.doc to determine if a document has content.
      if (!script || !script.editorState) {
        return false;
      }

      if (script.editorState.doc === "") {
        return false;
      }

      return true;
    });

    if (!someDocumentHasContent) {
      removeAllDocuments(exceptDocumentIds);
      return;
    }

    if (exceptDocumentIds.length === 0) {
      setModal({
        title: t("delete_document_confirmation.all.title"),
        message: t("delete_document_confirmation.all.message"),
        confirmButtonText: t("delete_document_confirmation.all.confirm"),
        onConfirm: () => {
          setModal(null);
          removeAllDocuments([]);
        },
      });
      return;
    }

    // all_other
    setModal({
      title: t("delete_document_confirmation.all_other.title"),
      message: t("delete_document_confirmation.all_other.message"),
      confirmButtonText: t("delete_document_confirmation.all_other.confirm"),
      onConfirm: () => {
        setModal(null);
        removeAllDocuments(exceptDocumentIds);
      },
    });
  };

  const handleDeleteDocument = (documentId: string) => {
    const document = documents.find(doc => doc.id === documentId);

    if (!document) {
      return;
    }

    const script = getDocumentScript(document, scriptsState);

    // We check editorState.doc to determine if a document has content.
    if (!script || !script.editorState) {
      removeDocument(document.id);
      return;
    }

    if (script.editorState.doc === "") {
      removeDocument(document.id);
      return;
    }

    setModal({
      title: t("delete_document_confirmation.single.title"),
      message: t("delete_document_confirmation.single.message"),
      confirmButtonText: t("delete_document_confirmation.single.confirm"),
      onConfirm: () => {
        setModal(null);
        removeDocument(document.id);
      },
    });
  };

  const renderModal = () => {
    if (!modal) {
      return null;
    }

    return (
      <ConfirmationModal
        onClose={() => {
          setModal(null);
        }}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        confirmButtonOptions={{
          text: modal.confirmButtonText,
        }}
      />
    );
  };

  const modalMarkup = renderModal();

  return {
    handleDeleteDocument,
    handleDeleteAllDocuments,
    modalMarkup,
  };
};

export { useDocumentsActionConfirmation };
